import type {
  RepositoryResource,
  TicketingSystem,
  VendorTicketingSystem
} from "./ticketing.types"

// Alphabetical: the platform selection offers systems in this order
export const TICKETING_SYSTEMS = [
  "asana",
  "azure_boards",
  "github",
  "jira"
] as const

const AZURE_BOARDS_HOSTS = ["dev.azure.com"]
const AZURE_BOARDS_HOST_SUFFIX = ".visualstudio.com"
const JIRA_CLOUD_HOST_SUFFIX = ".atlassian.net"
export const ASANA_HOST = "app.asana.com"
// github.com and GitHub Enterprise Server share the issue path
const GITHUB_ISSUE_PATH = /^\/[^/]+\/[^/]+\/issues\/\d+\/?$/

export const isTicketingSystem = (value: unknown): value is TicketingSystem =>
  TICKETING_SYSTEMS.some((system) => system === value)

export const isVendorTicketingSystem = (
  value: unknown
): value is VendorTicketingSystem =>
  isTicketingSystem(value) && value !== "jira"

const parseUrl = (url: string) => {
  try {
    const { hostname, pathname } = new URL(url)
    return { host: hostname.toLowerCase(), path: pathname }
  } catch {
    return { host: "", path: "" }
  }
}

// Tickets carry no vendor field; the URL host is unambiguous (atlassian.net for Jira),
// except GitHub Enterprise Server, recognised by its issue path.
export const getTicketingSystemFromUrl = (url?: string): TicketingSystem => {
  const { host, path } = parseUrl(url || "")

  if (
    AZURE_BOARDS_HOSTS.includes(host) ||
    host.endsWith(AZURE_BOARDS_HOST_SUFFIX)
  )
    return "azure_boards"
  if (host === ASANA_HOST) return "asana"
  if (host.endsWith(JIRA_CLOUD_HOST_SUFFIX)) return "jira"
  if (GITHUB_ISSUE_PATH.test(path)) return "github"

  return "jira"
}

// A GitHub issue goes to the Kodem issue's repository: there is no project to pick
export const hasTicketProjects = (system: TicketingSystem) =>
  system !== "github"

// CLI and CI scans of a local checkout name its repository "owner/repo (Local)"
export const LOCAL_REPOSITORY_SUFFIX = " (Local)"

// a free-text --code-repository-name that isn't owner/repo can't be a GitHub repository
const OWNER_REPO = /^[^/]+\/[^/]+$/

// owner/repo is the code repository's full name; its name differs for a monorepo project
export const getGitHubRepository = (resources: RepositoryResource[] = []) => {
  const repository = resources.find(({ type }) => type === "CodeRepository")

  if (!repository) return undefined

  const { resourceName, sourceControl } = repository

  if (resourceName.endsWith(LOCAL_REPOSITORY_SUFFIX)) {
    const name = resourceName.slice(0, -LOCAL_REPOSITORY_SUFFIX.length).trim()
    return OWNER_REPO.test(name) ? name : undefined
  }

  return sourceControl.toLowerCase() === "github" ? resourceName : undefined
}

const canFileGitHubIssue = (
  resources: RepositoryResource[] | undefined,
  issuesDisabledOwners: string[]
) => {
  const repository = getGitHubRepository(resources)

  if (!repository) return false

  const [owner] = repository.split("/")

  return !issuesDisabledOwners.includes(owner.toLowerCase())
}

// GitHub is offered only for an issue it can file in: one of a GitHub repository whose
// owner (lowercase in issuesDisabledOwners) allows creating issues
export const getIssueTicketingSystems = (
  systems: TicketingSystem[],
  resources?: RepositoryResource[],
  issuesDisabledOwners: string[] = []
) =>
  canFileGitHubIssue(resources, issuesDisabledOwners)
    ? systems
    : systems.filter((system) => system !== "github")

// Several issues may span projects: a system is offered when it fits one of them
export const getIssuesTicketingSystems = (
  systems: TicketingSystem[],
  projectsResources: RepositoryResource[][],
  issuesDisabledOwners: string[] = []
) => {
  const offered = [
    getIssueTicketingSystems(systems),
    ...projectsResources.map((resources) =>
      getIssueTicketingSystems(systems, resources, issuesDisabledOwners)
    )
  ].flat()

  return systems.filter((system) => offered.includes(system))
}

export const getTicketingSystemsFromUrls = (urls: string[]) => [
  ...new Set(urls.map(getTicketingSystemFromUrl))
]

// GitHub counts as connected only while one of its organizations allows creating issues
export const getAllowedTicketingSystems = (
  systems: TicketingSystem[],
  isGitHubIssuesAllowed: boolean
) =>
  isGitHubIssuesAllowed
    ? systems
    : systems.filter((system) => system !== "github")

export const getConnectedTicketingSystems = (
  integrationsStatus: Record<string, boolean> = {}
): TicketingSystem[] =>
  TICKETING_SYSTEMS.filter((system) => !!integrationsStatus[system])

// Jira links by key, so a pasted URL is cut to its last segment; the other vendors
// resolve a full ticket URL themselves (an Asana URL can end with "/f").
export const getIssueKeyFromInput = (
  system: TicketingSystem,
  value: string
) => {
  const trimmed = value.trim()

  if (system === "jira") return trimmed.split("/").slice(-1)[0]

  return trimmed
}
